<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="520"
    persistent
  >
    <v-card class="share-card">
      <v-card-title class="dialog-title">
        Share this chat
      </v-card-title>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          class="mb-4"
          border="start"
          colored-border
          elevation="1"
          density="compact"
        >
          {{ error }}
        </v-alert>

        <div
          v-if="loading"
          class="loading-container"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </div>

        <div v-else-if="shareLink">
          <v-text-field
            v-model="shareLink"
            label="Sharable link"
            readonly
            variant="outlined"
            density="comfortable"
            append-inner-icon="mdi-content-copy"
            :hint="copied ? 'Copied!' : ''"
            persistent-hint
            class="share-input"
            @click:append-inner="handleCopy"
          />

          <div class="btn-row">
            <v-btn
              color="error"
              variant="text"
              @click="handleDelete"
            >
              Delete Link
            </v-btn>
          </div>
        </div>

        <div
          v-else
          class="empty-state"
        >
          <p class="text-hint">
            This chat has no shared link yet.
          </p>
          <v-btn
            color="primary"
            @click="handleCreate"
          >
            Create Shareable Link
          </v-btn>
        </div>
      </v-card-text>

      <v-card-actions class="footer">
        <v-spacer />
        <v-btn
          variant="text"
          @click="dialogVisible = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { useShareChat } from '@/interfaces/composables/useShareChat';

  const props = defineProps<{ modelValue: boolean }>();
  const emit = defineEmits(['update:modelValue']);

  const dialogVisible = ref(props.modelValue);

  watch(
    () => props.modelValue,
    (val) => {
      dialogVisible.value = val;
      if (val) {
        loadSharedLink();
      }
    }
  );

  watch(dialogVisible, (val) => {
    emit('update:modelValue', val);
  });

  const {
    shareLink,
    createSharedLink,
    deleteSharedLink,
    copyShareLink,
    loadSharedLink,
    loading,
    error,
  } = useShareChat();

  const copied = ref(false);

  const handleCopy = async () => {
    await copyShareLink();
    copied.value = true;
    setTimeout(() => (copied.value = false), 2000);
  };

  const handleCreate = async () => {
    await createSharedLink();
  };

  const handleDelete = async () => {
    await deleteSharedLink();
  };
</script>

<style scoped lang="sass">
  .share-card
    padding: 20px
    border-radius: 16px

  .dialog-title
    font-size: 20px
    font-weight: 600
    margin-bottom: 12px

  .loading-container
    display: flex
    justify-content: center
    align-items: center
    padding: 30px 0

  .empty-state
    text-align: center
    padding: 20px 0

  .text-hint
    color: #777
    margin-bottom: 16px
    font-size: 14px

  .share-input
    margin-bottom: 10px

  .btn-row
    display: flex
    justify-content: flex-end
    margin-top: 4px

  .footer
    padding-top: 16px
</style>
